import { Alert, AlertTitle, Grid, TextField, Typography } from '@mui/material'
import { type Control, Controller } from 'react-hook-form'

import { Box } from '@/components/ui'
import { validateEmail } from '@/utils/emailUtils'

import {
  type CreateOrgFormValues,
  TEMPORARY_PASSWORD_MIN_LENGTH,
} from '../definitions/createOrganizationForm'

interface AdminUserStepProps {
  /**
   * Set when the organization turned out to already have staff, which the
   * endpoint refuses to add its first admin to. There is nothing left to collect,
   * so the fields give way to saying so.
   */
  alreadyExists?: boolean
  control: Control<CreateOrgFormValues>
  /**
   * Set when creating the admin has already failed for this organization. What
   * came before it is saved at this point, which is the part the user needs
   * told plainly.
   */
  failureMessage?: string
  organizationName: string
  /**
   * Whether the organization predates this run, which decides what a failure here
   * can say survived.
   */
  organizationOnRecord: boolean
  /** The primary location the admin is assigned, or null when there is none. */
  primaryLocationName: string | null
}

const requireValue = (value: string) => value.trim().length > 0 || 'Required.'

const requireTemporaryPassword = (value: string) =>
  value.length >= TEMPORARY_PASSWORD_MIN_LENGTH ||
  `Must be at least ${TEMPORARY_PASSWORD_MIN_LENGTH} characters.`

/**
 * Left readable rather than masked: whoever types it has to read it back to hand
 * it over, and a mistyped one cannot be recovered — there is no reset flow yet.
 */
const TEMPORARY_PASSWORD_HELP =
  `At least ${TEMPORARY_PASSWORD_MIN_LENGTH} characters. Give it to them directly — ` +
  'they are made to choose a new one when they first sign in.'

/** `validateEmail` returns '' when the address is usable, so no error is a pass. */
const requireEmail = (value: string) => {
  const error = validateEmail(value)
  return error === '' || error
}

export const AdminUserStep = ({
  alreadyExists,
  control,
  failureMessage,
  organizationName,
  organizationOnRecord,
  primaryLocationName,
}: AdminUserStepProps) => {
  if (alreadyExists) {
    return (
      <Alert severity='info'>
        <AlertTitle>{organizationName} already has its admin user</AlertTitle>
        {organizationOnRecord
          ? 'Its administrator was created before this run. '
          : 'Most likely an earlier attempt here saved it and its response never arrived. '}
        Nothing more is needed: an organization keeps the first admin it is given, and
        every staff member after them is added by that admin, not from here.
      </Alert>
    )
  }

  return (
    <Box variant='flexColumn' gap='md'>
      {failureMessage && (
        <Alert severity='error'>
          <AlertTitle>
            {organizationOnRecord
              ? `${organizationName} has no admin user yet`
              : primaryLocationName
                ? `${organizationName} and its location were created, but its admin user was not`
                : `${organizationName} was created, but its admin user was not`}
          </AlertTitle>
          {failureMessage} Nothing already created needs redoing — correct the details below
          and try again.
        </Alert>
      )}
      <Typography variant='body2' color='text.secondary'>
        This is the practice&apos;s own administrator, who manages every other staff member
        from here on. Their name, username, email, and a temporary password are needed —
        the rest they can fill in themselves.
      </Typography>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Controller
            name='admin_name_given'
            control={control}
            rules={{ validate: requireValue }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label='First Name'
                placeholder='Dana'
                required
                fullWidth
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name='admin_name_family'
            control={control}
            rules={{ validate: requireValue }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label='Last Name'
                placeholder='Okonkwo'
                required
                fullWidth
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name='admin_username'
            control={control}
            rules={{ validate: requireValue }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label='Username'
                placeholder='dokonkwo'
                required
                fullWidth
                error={fieldState.invalid}
                helperText={fieldState.error?.message ?? 'What they sign in with.'}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name='admin_email'
            control={control}
            rules={{ validate: requireEmail }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label='Email'
                type='email'
                placeholder='dana@acmeeyecare.com'
                required
                fullWidth
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
        <Grid size={12}>
          <Controller
            name='admin_temporary_password'
            control={control}
            rules={{ validate: requireTemporaryPassword }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label='Temporary Password'
                required
                fullWidth
                error={fieldState.invalid}
                helperText={fieldState.error?.message ?? TEMPORARY_PASSWORD_HELP}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name='admin_npi'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label='NPI'
                placeholder='1234567890'
                fullWidth
                helperText='Optional — providers only.'
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name='admin_license_number'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label='License Number'
                fullWidth
                helperText='Optional.'
              />
            )}
          />
        </Grid>
      </Grid>
      {primaryLocationName && (
        <Typography variant='body2' color='text.secondary'>
          They will be assigned to {primaryLocationName}, the organization&apos;s primary
          location.
        </Typography>
      )}
    </Box>
  )
}
